import React from 'react';
import { FlatList } from 'react-native';
import { twMerge } from 'tailwind-merge';

import {
  PROMOTIONAL_SLIDES,
  DOT_SIZE,
  SLIDE_INTERVAL_MS,
  PromotionalCarouselSlide,
} from './PromotionalCarouselSlide';

import { HStack } from '@/components/ui/hstack';
import { Pressable } from '@/components/ui/pressable';
import { VStack } from '@/components/ui/vstack';
import { useColors } from '@/hooks/useColors';

/**
 * Web promotional carousel with auto-advancing slides and pagination dots.
 *
 * Uses ResizeObserver for responsive width measurement with window resize fallback.
 * Supports dark mode styling and 4-second auto-advance timing.
 */
type PromotionalCarouselProps = {
  onDark?: boolean;
};

export default function PromotionalCarousel({ onDark = false }: PromotionalCarouselProps) {
  const { colors } = useColors();

  const [containerWidth, setContainerWidth] = React.useState<number>(0);

  const [index, setIndex] = React.useState(0);

  const listRef = React.useRef<FlatList>(null);

  const timerRef = React.useRef<ReturnType<typeof setInterval> | null>(null);

  const containerRef = React.useRef<HTMLDivElement | null>(null);

  React.useEffect(() => {
    const el = containerRef.current;

    if (!el) {
      return;
    }

    const measure = () => {
      const w = Math.round(el.getBoundingClientRect().width);

      setContainerWidth(w);
    };

    measure();

    let ro: ResizeObserver | null = null;

    let onResize: (() => void) | null = null;

    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(() => measure());

      ro.observe(el);
    } else if (typeof window !== 'undefined') {
      onResize = () => measure();

      window.addEventListener('resize', onResize);
    }

    return () => {
      if (ro) {
        ro.disconnect();
      }

      if (onResize) {
        window.removeEventListener('resize', onResize);
      }
    };
  }, []);

  React.useEffect(() => {
    if (!containerWidth) {
      return;
    }

    if (timerRef.current) {
      clearInterval(timerRef.current);
    }

    timerRef.current = setInterval(() => {
      setIndex((prev) => {
        const next = (prev + 1) % PROMOTIONAL_SLIDES.length;

        const isWrapping = next === 0 && prev === PROMOTIONAL_SLIDES.length - 1;

        listRef.current?.scrollToOffset({
          offset: next * containerWidth,
          animated: !isWrapping,
        });

        return next;
      });
    }, SLIDE_INTERVAL_MS);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [containerWidth]);

  return (
    <VStack
      ref={containerRef as unknown as React.RefObject<any>}
      className="min-h-[420px] w-full items-center justify-center">
      {containerWidth > 0 ? (
        <FlatList
          key={`w-${containerWidth}`}
          ref={listRef}
          horizontal
          pagingEnabled
          showsHorizontalScrollIndicator={false}
          style={{ width: containerWidth }}
          snapToInterval={containerWidth}
          snapToAlignment="start"
          decelerationRate="fast"
          getItemLayout={(_, i) => ({
            length: containerWidth,
            offset: containerWidth * i,
            index: i,
          })}
          data={PROMOTIONAL_SLIDES}
          keyExtractor={(item) => item.title}
          onMomentumScrollEnd={(e) => {
            const newIndex = Math.round(e.nativeEvent.contentOffset.x / containerWidth);

            if (newIndex !== index) {
              setIndex(newIndex);
            }
          }}
          renderItem={({ item }) => (
            <PromotionalCarouselSlide width={containerWidth} onDark={onDark} {...item} />
          )}
        />
      ) : null}

      <HStack className={twMerge('items-center justify-center gap-2', 'mt-12')}>
        {PROMOTIONAL_SLIDES.map((_, i) => {
          const isActive = i === index;

          const getDotColor = (onDark: boolean, isActive: boolean) => {
            if (onDark && isActive) {
              return colors.typography[0];
            }

            if (onDark && !isActive) {
              return 'rgba(255,255,255,0.45)';
            }

            if (!onDark && isActive) {
              return colors.primary[500];
            }

            return colors.typography[300];
          };

          const bg = getDotColor(onDark, isActive);

          return (
            <Pressable
              key={i}
              accessibilityLabel={`Go to slide ${i + 1}`}
              onPress={() => {
                setIndex(i);

                listRef.current?.scrollToOffset({
                  offset: i * containerWidth,
                  animated: true,
                });
              }}
              className="items-center justify-center"
              style={{ width: DOT_SIZE + 8, height: DOT_SIZE + 8 }}>
              <VStack
                className="rounded-full"
                style={{ width: DOT_SIZE, height: DOT_SIZE, backgroundColor: bg }}
              />
            </Pressable>
          );
        })}
      </HStack>
    </VStack>
  );
}
